import React, { useState, useRef, useEffect } from "react";
import { useAppContext } from "../context/AppContext";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { PageHeader } from "../components/ui/PageHeader";
import { CameraFeed } from "../components/CameraFeed";
import { useCameraSetup } from "../hooks/useCameraSetup";
// import { useCameraRetry } from "../hooks/useCameraRetry";
import { useNavigationBlocker } from "../hooks/useNavigationBlocker";
import { ScanResultModal } from "../components/ScanResultModal";
import apiService from "../services/ApiService";
import useHandheldBarcode from "../hooks/useHandheldBarcode";
import { Modal } from "../components/ui/Modal";
import { Input } from "../components/ui/Input";
import type { ScannedBarcode } from "../types/BarcodeDetection";

// Define interfaces for component state
interface SessionStats {
  passedCount: number;
  totalCount: number;
}

export const MultiCameraOperatorView: React.FC = () => {
  const { operatorName, cameraConfig } = useAppContext();
  const [allCodesScanned, setAllCodesScanned] = useState(false);
  const [finalResult, setFinalResult] = useState<
    "pass" | "fail" | "lock" | null
  >(null);
  const [showResultModal, setShowResultModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isExiting, setIsExiting] = useState(false);
  const [currentShiftId, setCurrentShiftId] = useState<string | null>(() =>
    localStorage.getItem("current_shift_id")
  );

  const [modalCodes, setModalCodes] = useState<string[]>(["", "", "", ""]);

  const [sessionStats, setSessionStats] = useState<SessionStats>({
    passedCount: 0,
    totalCount: 0,
  });

  // Track current barcodes in real-time
  const currentBarcodes = useRef<ScannedBarcode[]>([
    { barcode: "", timestamp: "", isManualOverride: false },
    { barcode: "", timestamp: "", isManualOverride: false },
    { barcode: "", timestamp: "", isManualOverride: false },
    { barcode: "", timestamp: "", isManualOverride: false },
  ]);

  // Add a ref to track if bitbox block has been recorded for current session
  const bitboxBlockRecorded = useRef(false);

  // Add a ref to track modal state to avoid stale closure issues
  const showResultModalRef = useRef(showResultModal);

  // NEW: Add ref to prevent concurrent camera initialization
  const isInitializingCameras = useRef(false);

  // Add a ref to track processing state per camera
  const cameraProcessing = useRef<boolean[]>([false, false, false]);

  // NEW: Track camera detection success for smart processing
  const cameraSuccessCount = useRef<number[]>([0, 0, 0]);
  const lastDetectionTime = useRef<number[]>([0, 0, 0]);

  // Use navigation blocker hook
  const { clearHistoryBuffer } = useNavigationBlocker({
    shouldBlock: showResultModal,
    message: "You have unsaved scan results. Are you sure you want to leave?",
  });

  useEffect(() => {
    console.log('showResultModal changed', showResultModal)
    showResultModalRef.current = showResultModal;
  }, [showResultModal])

  // Camera setup hook
  const {
    videoRef1,
    videoRef2,
    videoRef3,
    cameraInitializationState,
    availableCameras,
    isInitializing,
    isBarcodeApiSupported,
    hasCamerasConfigured,
    cameraFeeds,
    updateCameraStatus,
    clearCameraStatus,
    resetCameraFeeds,
    // initializeCameras,
    cleanupCameraStreams,
    handleVideoError,
  } = useCameraSetup({
    cameraConfig,
    onBarcodeDetected: async (cameraIndex, barcode) => {
      // Prevent concurrent processing for the same camera
      if (cameraProcessing.current[cameraIndex]) {
        console.log(`Camera ${cameraIndex} is already processing, skipping...`);
        return;
      }

      cameraProcessing.current[cameraIndex] = true;

      try {
        cameraSuccessCount.current[cameraIndex]++;
        lastDetectionTime.current[cameraIndex] = Date.now();
        updateCameraStatus(cameraIndex, barcode, true);

        currentBarcodes.current[cameraIndex] = {
          barcode,
          timestamp: new Date().toISOString(),
          isManualOverride: false,
        };

        // console.log(`Camera ${cameraIndex} scanned:`, barcode);

        const allHaveBarcodes = currentBarcodes.current.every(
          (code) => code.barcode && code.barcode.trim() !== ""
        );
        if (allHaveBarcodes && !bitboxBlockRecorded.current && !showResultModalRef.current) {
          console.log('onbscan', allHaveBarcodes, bitboxBlockRecorded.current, showResultModal)
          bitboxBlockRecorded.current = true;

          const codes = [...currentBarcodes.current] as [
            ScannedBarcode,
            ScannedBarcode,
            ScannedBarcode,
            ScannedBarcode
          ];
          const allMatch = codes
            .slice(0, 3)
            .every(
              (code) =>
                code.barcode.slice(0, 9) === codes[0].barcode.slice(0, 9)
            );

          setAllCodesScanned(true);
          setFinalResult(allMatch ? "pass" : "fail");

          setModalCodes([...codes.map((c) => c.barcode)]);

          setSessionStats(
            (prev: SessionStats): SessionStats => ({
              passedCount: allMatch ? prev.passedCount + 1 : prev.passedCount,
              totalCount: prev.totalCount + 1,
            })
          );

          console.log('trackBitboxBlock called from onBarcodeDetected', codes);

          trackBitboxBlock(codes).catch((error) => {
            console.error("Failed to track bitbox block:", error);
            bitboxBlockRecorded.current = false;
          });
        }
      } finally {
        cameraProcessing.current[cameraIndex] = false;
      }
    },
    onBarcodeCleared: (cameraIndex) => {
      // console.log(`Clearing barcode for camera ${cameraIndex}`);

      cameraProcessing.current[cameraIndex] = false;

      if (!showResultModalRef.current) {
        clearCameraStatus(cameraIndex);
        currentBarcodes.current[cameraIndex] = {
          barcode: "",
          timestamp: "",
          isManualOverride: false,
        };

        bitboxBlockRecorded.current = false;

        if (allCodesScanned) {
          setAllCodesScanned(false);
          setFinalResult(null);
        }
      }
    },
  });

  // Retry initializing cameras if needed
  // const { retryInitializingCameras } = useCameraRetry({
  //   isInitializing,
  //   cameraConfig,
  //   initializeCameras,
  //   initializeDetectors: () => { }, // No-op for this view
  //   stopAllDetection: () => { }, // No-op for this view
  //   cleanupCameraStreams,
  //   resetCameraFeeds,
  //   onSessionReset: () => {
  //     setAllCodesScanned(false);
  //     setFinalResult(null);
  //     currentBarcodes.current = [
  //       { barcode: "", timestamp: "", isManualOverride: false },
  //       { barcode: "", timestamp: "", isManualOverride: false },
  //       { barcode: "", timestamp: "", isManualOverride: false },
  //       { barcode: "", timestamp: "", isManualOverride: false },
  //     ];
  //     setModalCodes(["", "", "", ""]);
  //     setScannedCode("");
  //     bitboxBlockRecorded.current = false;
  //     cameraProcessing.current = [false, false, false];
  //     isInitializingCameras.current = false;
  //   },
  //   setIsInitializing: (value) => setIsLoading(value),
  // });

  // const handleFakeReloadCameras = () => {
  //   window.location.reload();
  // }

  // NEW: Enhanced camera reload with proper cleanup
  const handleReloadCameras = async () => {
    // Prevent concurrent initialization attempts
    if (isInitializingCameras.current) {
      console.log("Camera initialization already in progress, skipping...");
      return;
    }

    isInitializingCameras.current = true;

    try {
      console.log("Starting camera reload...");

      cleanupCameraStreams();
      resetCameraFeeds();

      cameraProcessing.current = [false, false, false];

      setAllCodesScanned(false);
      setFinalResult(null);
      currentBarcodes.current = [
        { barcode: "", timestamp: "", isManualOverride: false },
        { barcode: "", timestamp: "", isManualOverride: false },
        { barcode: "", timestamp: "", isManualOverride: false },
        { barcode: "", timestamp: "", isManualOverride: false },
      ];
      setModalCodes(["", "", "", ""]);
      setScannedCode("");
      bitboxBlockRecorded.current = false;

      await new Promise((resolve) => setTimeout(resolve, 500));

      window.location.reload();

      // await retryInitializingCameras();

      console.log("Camera reload completed successfully");
    } catch (error) {
      console.error("Error during camera reload:", error);
    } finally {
      isInitializingCameras.current = false;
    }
  };

  const trackBitboxBlock = async (
    codes: [ScannedBarcode, ScannedBarcode, ScannedBarcode, ScannedBarcode]
  ) => {
    if (!currentShiftId || isLoading) return;

    setTimeout(async () => {
      try {
        setIsLoading(true);
        const bitboxData = {
          barcode_data: codes.map((code, index) => ({
            code: code.barcode,
            slotId: `Camera ${index + 1}`,
            sessionId: localStorage.getItem("current_shift_id") as string,
            timestamp: code.timestamp,
            isManualOverride: code.isManualOverride,
          })),
        };

        if (apiService.isAuthenticated()) {
          // Reduce timeout to prevent long waits
          const timeoutPromise = new Promise((_, reject) =>
            setTimeout(() => reject(new Error("API timeout")), 5000)
          );

          const apiPromise = apiService.recordBitboxBlock(bitboxData);
          await Promise.race([apiPromise, timeoutPromise]);

          console.log("Bitbox block recorded to API:", bitboxData);
        }
      } catch (error) {
        console.error("Failed to record bitbox block to API:", error);
        // Reset flag on error so it can be retried
        bitboxBlockRecorded.current = false;
      } finally {
        setIsLoading(false);
        setShowResultModal(true);
      }
    }, 0);
  };

  const scannedHandler = () => {
    const allHaveBarcodes = currentBarcodes.current.every(
      (code) => code.barcode && code.barcode.trim() !== ""
    );

    if (allHaveBarcodes && !bitboxBlockRecorded.current && !showResultModalRef.current) {
      bitboxBlockRecorded.current = true;

      const codes = [...currentBarcodes.current] as [
        ScannedBarcode,
        ScannedBarcode,
        ScannedBarcode,
        ScannedBarcode
      ];
      const allMatch = codes
        .slice(0, 3)
        .every(
          (code) => code.barcode.slice(0, 9) === codes[0].barcode.slice(0, 9)
        );

      setAllCodesScanned(true);
      setFinalResult(allMatch ? "pass" : "fail");

      setModalCodes([...codes.map((c) => c.barcode)]);

      setSessionStats(
        (prev: SessionStats): SessionStats => ({
          passedCount: allMatch ? prev.passedCount + 1 : prev.passedCount,
          totalCount: prev.totalCount + 1,
        })
      );

      console.log('trackBitboxBlock called from scannedHandler', codes);

      trackBitboxBlock(codes).catch((error) => {
        console.error("Failed to track bitbox block:", error);
        bitboxBlockRecorded.current = false;
      });
    }
  };


  const {
    scannedCode,
    setScannedCode,
    preventListener: preventHandheldListener,
    showManualInput,
    setManualCode,
    setShowManualInput,
    handleManualSubmit,
    manualCode,
  } = useHandheldBarcode(currentBarcodes, scannedHandler);

  const handleShiftEnd = async () => {
    if (currentShiftId && apiService.isAuthenticated()) {
      try {
        await apiService.updateShiftStatus(currentShiftId, {
          new_status: "done",
        });
      } catch (error) {
        console.error("Failed to end shift:", error);
        // Continue with navigation even if API call fails
      }
    }
  };

  const checkShiftDetail = async () => {
    if (currentShiftId && apiService.isAuthenticated()) {
      try {
        // Add timeout for shift detail check
        const timeoutPromise = new Promise((_, reject) =>
          setTimeout(() => reject(new Error("Shift detail timeout")), 5000)
        );

        const detailPromise = apiService.getShiftDetail(currentShiftId);
        const shiftDetail = await Promise.race([detailPromise, timeoutPromise]);

        console.log("Current shift detail:", shiftDetail);

        // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-expect-error
        if (shiftDetail?.status === "locked") {
          setFinalResult("lock");
          setShowResultModal(true);
        }

        return shiftDetail;
      } catch (error) {
        console.error("Failed to fetch shift detail:", error);
      }
    }
    return null;
  };

  // Handle exit button click
  const handleExit = async () => {
    // Prevent multiple exit attempts
    if (isExiting) return;

    setIsExiting(true);

    try {
      // Stop cameras immediately for better UX
      cleanupCameraStreams();
      // Reset initialization flag
      isInitializingCameras.current = false;
      await handleShiftEnd();
      
      // Logout verificator from API if authenticated
      if (apiService.isAuthenticated()) {
        apiService.logoutForRole("verificator");
        console.log("Logged out verificator from API");
      }
    } catch (error) {
      cleanupCameraStreams();
      console.error("Error during exit:", error);
      // Force navigation on error after cleanup
    } finally {
      setIsExiting(false);
      localStorage.removeItem("current_shift_id");
      localStorage.removeItem("current_verificator");
      setCurrentShiftId(null);
      window.location.href = "/"; // Force reload to reset state
    }
  };

  // Handle continue scanning (reset for new scan)
  const handleContinueScanning = () => {
    console.log("handleContinueScanning called");

    // Clear processing locks
    cameraProcessing.current = [false, false, false];

    // NEW: Reset success tracking
    cameraSuccessCount.current = [0, 0, 0];
    lastDetectionTime.current = [0, 0, 0];

    // Clear the navigation blocking and history buffer
    clearHistoryBuffer();

    // Reset modal and scanning states
    setShowResultModal(false);
    setAllCodesScanned(false);
    setFinalResult(null);

    // Reset barcode tracking
    currentBarcodes.current = [
      { barcode: "", timestamp: "", isManualOverride: false },
      { barcode: "", timestamp: "", isManualOverride: false },
      { barcode: "", timestamp: "", isManualOverride: false },
      { barcode: "", timestamp: "", isManualOverride: false },
    ];

    // Reset modal codes
    setModalCodes(["", "", "", ""]);
    setScannedCode("");

    // Reset camera feeds
    resetCameraFeeds();

    // Reset bitbox block flag for new session
    bitboxBlockRecorded.current = false;
  };

  // Handle manual input from camera feeds
  const handleManualInput = async (cameraIndex: number, code: string) => {
    // Prevent concurrent processing

    if (cameraProcessing.current[cameraIndex]) return;

    cameraProcessing.current[cameraIndex] = true;

    preventHandheldListener.current = true;
    try {
      // Mark as "read" for manual input
      updateCameraStatus(cameraIndex, code, true);

      // Update the real-time barcode tracking
      currentBarcodes.current[cameraIndex] = {
        barcode: code,
        timestamp: new Date().toISOString(),
        isManualOverride: true,
      };

      // Check if all four slots now have barcodes
      const allHaveBarcodes = currentBarcodes.current.every(
        (code) => code.barcode && code.barcode.trim() !== ""
      );
      console.log(
        "All codes scanned (manual):",
        currentBarcodes.current.slice(0, 3)
      );

      if (allHaveBarcodes && !bitboxBlockRecorded.current && !showResultModalRef.current) {
        // Prevent multiple calls by setting the flag immediately
        bitboxBlockRecorded.current = true;

        const codes = [...currentBarcodes.current] as [
          ScannedBarcode,
          ScannedBarcode,
          ScannedBarcode,
          ScannedBarcode
        ];
        const allMatch = codes
          .slice(0, 3)
          .every(
            (code) => code.barcode.slice(0, 9) === codes[0].barcode.slice(0, 9)
          );

        console.log(allMatch ? "All codes match" : "Codes do not match");

        // Set the states immediately
        setAllCodesScanned(true);
        setFinalResult(allMatch ? "pass" : "fail");

        // Store the codes for the modal before showing it
        setModalCodes([...codes.map((c) => c.barcode)]);

        // Update session statistics for manual input
        setSessionStats(
          (prev: SessionStats): SessionStats => ({
            passedCount: allMatch ? prev.passedCount + 1 : prev.passedCount,
            totalCount: prev.totalCount + 1,
          })
        );

        // Track bitbox block in API (non-blocking)
        console.log('trackBitboxBlock called from handleManualInput', codes);
        trackBitboxBlock(codes).catch((error) => {
          console.error("Failed to track bitbox block:", error);
          bitboxBlockRecorded.current = false;
        });
      }
    } finally {
      cameraProcessing.current[cameraIndex] = false;
      preventHandheldListener.current = false;
    }
  };

  useEffect(() => {
    checkShiftDetail();
  }, [currentShiftId]);

  // Render a camera feed
  const renderCameraFeed = (feedIndex: number) => {
    const feed = cameraFeeds[feedIndex];

    // Use the appropriate ref based on index
    const videoRef =
      feedIndex === 0 ? videoRef1 : feedIndex === 1 ? videoRef2 : videoRef3;

    // Determine display status
    let displayStatus;
    if (allCodesScanned && finalResult) {
      // Show pass/fail for all feeds when comparison is complete
      displayStatus = finalResult;
    } else if (feed.barcode) {
      // Show "read" for individual scans before comparison
      displayStatus = "read";
    } else {
      // Show "no read" for empty slots
      displayStatus = "no read";
    }

    return (
      <CameraFeed
        key={feed.id}
        label={feed.label}
        status={displayStatus as "pass" | "fail" | "read" | "no read"}
        barcode={feed.barcode}
        lastScanTime={feed.lastScanTime}
        cameraState={cameraInitializationState[feedIndex]}
        videoRef={videoRef}
        onVideoError={() => handleVideoError(feedIndex)}
        onManualInput={(code) => handleManualInput(feedIndex, code)}
      />
    );
  };

  const getStatusClass = (status: string) => {
    if (status === "pass") return "bg-green-500 text-white";
    if (status === "fail") return "bg-red-500 text-white";
    if (status === "read") return "bg-orange-500 text-white";
    if (status === "no read") return "bg-gray-400 text-white";
    return "bg-gray-200 text-gray-700";
  };

  return (
    <div className="h-screen w-screen overflow-hidden p-4 bg-gray-50">
      <PageHeader
        title={
          <div className="flex items-center gap-4">
            <span>LumiBot - Verificator: {operatorName}</span>
            {currentShiftId && (
              <span className="text-sm bg-blue-100 text-blue-800 px-2 py-1 rounded-full">
                Shift: {currentShiftId.slice(-8)}
              </span>
            )}
            {isExiting && (
              <span className="text-sm bg-orange-100 text-orange-800 px-2 py-1 rounded-full">
                Exiting...
              </span>
            )}
          </div>
        }
        onExit={handleExit}
      />

      <Card className="mx-auto max-w-[80vw] px-4 flex flex-col">
        <div className="flex justify-between items-center">
          {/* Session Stats Display */}
          <div className="flex gap-2 items-center">
            <h3 className="text-lg font-medium">Passed Sets:</h3>
            <div className="flex items-center gap-2">
              <div className="text-3xl font-bold text-green-600">
                {sessionStats.passedCount}
              </div>
              <div className="text-lg text-gray-500">
                / {sessionStats.totalCount} total
              </div>
            </div>
          </div>
          <div className="flex items-center gap-4">
            <div className="text-sm text-gray-600">
              {availableCameras === 0 ? (
                <span className="text-red-500">No cameras detected</span>
              ) : (
                <span>
                  <span className="font-medium">{availableCameras}</span> camera
                  {availableCameras !== 1 ? "s" : ""} detected
                </span>
              )}
              {!isBarcodeApiSupported && (
                <span className="ml-2 bg-yellow-100 text-yellow-800 px-2 py-1 rounded text-xs">
                  Barcode API not supported
                </span>
              )}
            </div>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleReloadCameras}
              disabled={isInitializing || isInitializingCameras.current}
              className="text-xs"
            >
              {isInitializing || isInitializingCameras.current
                ? "Initializing..."
                : "Reload Cameras"}
            </Button>
          </div>
        </div>

        {/* Camera Configuration Notice */}
        {!hasCamerasConfigured && (
          <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-md">
            <div className="flex">
              <div className="ml-3">
                <h3 className="text-sm font-medium text-yellow-800">
                  No Cameras Configured
                </h3>
                <div className="mt-2 text-sm text-yellow-700">
                  <p>
                    Please ask your manager to configure cameras in the Manager
                    Settings.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Camera feeds */}
        <div className="space-y-4">
          {cameraFeeds.map((_, index) => renderCameraFeed(index))}
          <div className="w-full flex items-center gap-2">
            <div className="w-2/3">
              <div
                className={`w-full h-[60px] flex items-center justify-center rounded-lg ${getStatusClass(
                  scannedCode.length > 0 ? "read" : "no read"
                )} text-2xl font-bold relative`}
              >
                {scannedCode.length > 0 ? "READ" : "NO READ"}
              </div>
            </div>
            <div className="p-5 bg-gray-100 rounded-lg h-[60px] font-mono text-xl w-1/3 relative">
              <div className="mb-2 flex flex-row justify-between items-center absolute top-2 right-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowManualInput(true)}
                  className="text-xs px-2 py-1 bg-gray-200 hover:bg-gray-300 !text-gray-400 border-none"
                >
                  Manual
                </Button>
              </div>
              {scannedCode.length > 0 ? scannedCode : "No code scanned yet"}
            </div>
          </div>
        </div>
      </Card>

      {/* Result Modal */}
      <ScanResultModal
        isOpen={showResultModal}
        result={finalResult}
        codes={modalCodes}
        onConfirm={handleContinueScanning}
      />
      <Modal
        isOpen={showManualInput}
        onClose={() => setShowManualInput(false)}
        title="Enter Barcode Manually"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => setShowManualInput(false)}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={handleManualSubmit}>
              Submit
            </Button>
          </>
        }
      >
        <Input
          value={manualCode}
          onChange={(e) => setManualCode(e.target.value)}
          placeholder="Enter barcode"
          autoFocus
        />
      </Modal>
    </div>
  );
};
